import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../api';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  InputAdornment,
} from '@mui/material';
import { Person, Email, Lock, ArrowBack } from '@mui/icons-material';

const RegistrationPage = () => {
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [retypePassword, setRetypePassword] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    const emailFromUrl = params.get('email');
    if (tokenFromUrl && emailFromUrl) {
      setToken(tokenFromUrl);
      setEmail(emailFromUrl);
    } else {
      setError('Invalid or missing invitation link. Please use the link sent to your email.');
      setTimeout(() => navigate('/login'), 3000);
    }
  }, [location, navigate]);

  const validatePasswordStrength = (password) => {
    const minLength = 8;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return password.length >= minLength && hasSpecialChar;
  };

  const extractErrorMessage = (errorObj) => {
    if (typeof errorObj === 'string') return errorObj;
    if (errorObj && typeof errorObj === 'object') {
      const firstKey = Object.keys(errorObj)[0];
      if (firstKey) {
        const message = errorObj[firstKey];
        return Array.isArray(message) ? message[0] : message;
      }
      return JSON.stringify(errorObj);
    }
    return 'An unknown error occurred';
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (password !== retypePassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (!validatePasswordStrength(password)) {
      setError('Password must be at least 8 characters long and include a special character');
      setIsLoading(false);
      return;
    }

    if (!username) {
      setError('Username is required');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/register/?token=${token}`,
        { username, first_name: firstName, last_name: lastName, email, password },
        { withCredentials: true }
      );

      if (response.data.success) {
        setError('');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(extractErrorMessage(response.data.error) || 'Registration failed');
      }
    } catch (err) {
      setError(extractErrorMessage(err.response?.data?.error) || 'Failed to register. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4, px: 2 }}>
      <Box sx={{ mx: 'auto', maxWidth: 440, width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary' }}
          >
            Back to Home
          </Button>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center', mb: 2 }}>
          Register
        </Typography>
        <Paper component="form" onSubmit={handleRegister} sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            disabled
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            size="small"
            placeholder="Enter your username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="First Name (optional)"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            size="small"
            placeholder="Enter your first name"
          />
          <TextField
            fullWidth
            label="Last Name (optional)"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            size="small"
            placeholder="Enter your last name"
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            size="small"
            placeholder="Enter your password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Retype Password"
            type="password"
            value={retypePassword}
            onChange={(e) => setRetypePassword(e.target.value)}
            required
            size="small"
            placeholder="Retype your password"
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{ py: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            {isLoading ? 'Registering...' : 'Register'}
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default RegistrationPage;
